/** biome-ignore-all lint/suspicious/noArrayIndexKey: We can use index here */
import { use$ } from '@legendapp/state/react';
import { Group, Select, type SelectProps } from '@mantine/core';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PATTERN_COLS, PATTERN_MINES, Pattern } from '../../constants';
import { autoBetState$, gameState$, manualBetState$ } from '../../state';
import { CheeseIcon } from './CheeseIcon';
import { CoinIcon } from './CoinIcon';

export type PatternSelectProps = Omit<SelectProps, 'data'>;

const renderSelectOption: SelectProps['renderOption'] = ({ option }) => {
  const pattern = Number(option.value) as Pattern;
  const mines = PATTERN_MINES[pattern];
  const cols = PATTERN_COLS[pattern];
  const coins = cols - mines;

  return (
    <Group justify="space-between" w="100%">
      {option.label}
      <Group gap="xs">
        {Array.from({ length: coins }).map((_, index) => (
          <CoinIcon
            data-test-id={`pattern-select-coin-icon-${index}`}
            h={24}
            key={`coin-${index}`}
            w={24}
            withAnimation={false}
          />
        ))}
        {Array.from({ length: mines }).map((_, index) => (
          <CheeseIcon
            data-test-id={`pattern-select-cheese-icon-${index}`}
            h={24}
            key={`cheese-${index}`}
            w={24}
            withAnimation={false}
          />
        ))}
      </Group>
    </Group>
  );
};

export const PatternSelect = ({ ...props }: PatternSelectProps) => {
  const { pattern, setPattern } = use$(gameState$);
  const isManualBetting = use$(manualBetState$.isBetting);
  const isAutoBetting = use$(autoBetState$.isAutoBetting);
  const { t } = useTranslation();

  const options = useMemo(() => {
    return [
      {
        label: t('patternSelect.option.Easy'),
        value: Pattern.EASY.toString(),
      },
      {
        label: t('patternSelect.option.Medium'),
        value: Pattern.MEDIUM.toString(),
      },
      {
        label: t('patternSelect.option.Hard'),
        value: Pattern.HARD.toString(),
      },
      {
        label: t('patternSelect.option.Expert'),
        value: Pattern.EXPERT.toString(),
      },
      {
        label: t('patternSelect.option.Master'),
        value: Pattern.MASTER.toString(),
      },
    ];
  }, [t]);

  return (
    <Select
      allowDeselect={false}
      data={options}
      data-test-id="difficulty-select"
      disabled={isManualBetting || isAutoBetting}
      label={t('patternSelect.difficulty')}
      labelProps={{
        'data-test-id': 'difficulty-select-label',
      }}
      onChange={(value) => setPattern(Number(value))}
      renderOption={renderSelectOption}
      value={pattern.toString()}
      {...props}
    />
  );
};

PatternSelect.displayName = 'PatternSelect';
